import { Navigate, useLocation } from "react-router-dom";

import {
  useAppProperties,
  useCollection,
  useRedirectGatedContent,
  useVideo,
  useVideoFiles,
} from "@/hooks";
import { useResponsiveness, useUser } from "@/providers/context";
import {
  ContentThumbnail,
  Description,
  ErrorPage,
  PageLoader,
  ResourcesButton,
  Skeleton,
} from "@/components";
import { Header } from "@/app/modules/Navigation";

import { cn, getContentThumbnailUrl } from "@/utils";
import { ITEM_TYPE_VIDEO, QUERY_PARENT_ID } from "@/const.ts";
import { SaveToPlaylistButton } from "@/app/modules/Playlists";
import { CommentButton, Comments } from "@/app/modules/Comments";
import { ScheduleButton } from "@/app/modules/Calendar";

import { UpNext } from "./components/UpNext.tsx";
import { HLSVideoPlayer } from "./components/HLSVideoPlayer.tsx";
import { CompleteButton } from "./components/CompleteButton.tsx";
import { useActionsLoading } from "@/app/modules/ContentDetails/hooks/useActionsLoading.tsx";
import { CommentsProps } from "../Comments/Comments.tsx";

interface AudioDetailsProps {
  id: number;
}

export const AudioDetails = ({ id }: AudioDetailsProps) => {
  const { data: audio, isLoading, isError } = useVideo(id);
  const { data: files } = useVideoFiles({
    video: audio,
    videoId: id,
  });
  const { drawerMargin, transitionStyle } = useResponsiveness();
  const { isVideoUnlocked, uid } = useUser();
  const isUnlocked = isVideoUnlocked(audio);
  const { getRedirectPath } = useRedirectGatedContent();

  const { search, state } = useLocation();
  const query = new URLSearchParams(search);
  const parentId = query.get(QUERY_PARENT_ID);
  const { data: parentCollection, isLoading: isParentCollectionLoading } =
    useCollection(parentId ? parseInt(parentId) : 0);

  const { loading: actionsLoading } = useActionsLoading({
    itemId: audio?.VideoId,
    itemType: ITEM_TYPE_VIDEO,
    dataSource: audio?.DataSource,
    sourceId: audio?.SourceId,
  });
  const { data: properties } = useAppProperties();

  if (isLoading) {
    return <PageLoader />;
  }

  if (!audio || isError) {
    return <ErrorPage code={404} />;
  }

  if (audio && !isUnlocked) {
    return (
      <Navigate
        to={getRedirectPath({
          item: { Type: ITEM_TYPE_VIDEO, ChildId: id.toString() },
        })}
        replace
      />
    );
  }

  if (audio && parentId && !parentCollection && !isParentCollectionLoading) {
    return <Navigate to={`/audio/${id}`} state={state} replace />;
  }

  const hlsUrl = (files ?? []).find((f) => f.Type === "HLS")?.URL;

  const commentsBelowPlayer =
    properties?.WebItemDetailsDesign === "CommentsBelowPlayer";
  const marginRight = commentsBelowPlayer ? "0" : drawerMargin;

  const commentsProps: CommentsProps = {
    dataSource: audio.DataSource,
    itemType: ITEM_TYPE_VIDEO,
    sourceId: audio.SourceId,
    itemId: audio.VideoId,
  };

  return (
    <div style={{ marginRight, transition: transitionStyle }}>
      <Header className="px-[28px]" />
      <div className={commentsBelowPlayer ? "flex justify-center" : ""}>
        <div
          className={cn(
            "flex h-full w-full flex-col px-[28px] pb-40 text-item-primary",
            commentsBelowPlayer ? "max-w-[1200px]" : "mx-auto max-w-[970px]",
          )}
        >
          <div className="w-full">
            <div className="aspect-video overflow-hidden rounded-[12px]">
              <ContentThumbnail
                thumbnail={getContentThumbnailUrl(audio, "large", "landscape")}
                className="w-full"
              />
            </div>
            <div className="h4-bold mt-[28px]">{audio.Title}</div>
            {audio.Description && (
              <Description desc={audio.Description} className="pt-[12px]" />
            )}
            <div className="mt-12 h-[90px]">
              {hlsUrl ? (
                <HLSVideoPlayer
                  src={hlsUrl!}
                  video={audio}
                  autoPlay={state?.autoPlay}
                />
              ) : (
                <Skeleton className="h-full w-full" />
              )}
            </div>
            {!!uid && (
              <div className="mt-7 flex gap-3">
                {actionsLoading ? (
                  <>
                    <Skeleton className="h-8 w-[72px]" />
                    <Skeleton className="h-8 w-[104px]" />
                    <Skeleton className="h-8 w-[101px]" />
                  </>
                ) : (
                  <>
                    <SaveToPlaylistButton
                      ItemId={audio.VideoId}
                      ItemType={ITEM_TYPE_VIDEO}
                      SourceItemId={audio.SourceId}
                    />
                    <CompleteButton video={audio} />
                    <ScheduleButton video={audio} />
                    {!commentsBelowPlayer && (
                      <CommentButton {...commentsProps} />
                    )}
                    <ResourcesButton resources={audio.Resources} />
                  </>
                )}
              </div>
            )}
            {commentsBelowPlayer ? (
              <>
                <Comments showHeader {...commentsProps} />
              </>
            ) : (
              <UpNext currentItemId={id} />
            )}
          </div>
        </div>
        {commentsBelowPlayer && <UpNext currentItemId={id} isSidePanel />}
      </div>
    </div>
  );
};
